import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { ModalActionTypes } from '@acorns/web-components'
import { domainMap } from 'config/env'
import { withFormik } from 'formik'
import { pathOr } from 'ramda'
import {
  branch,
  compose,
  lifecycle,
  renderNothing,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import { REDIRECTION_URL_KEY } from 'app-constants'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralDisclosureRequired from 'components/HOCs/WithReferralDisclosureRequired'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorHandler } from 'utils/Apollo/apolloErrorHandlingProvider'
import { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withOptimizely } from 'utils/Optimizely'
import withTieredRegistration from 'utils/TieredRegistration'

import formRows, { handleSubmit, mapPropsToValues, validationSchema } from './form'
import registrationComplete from './gql/registrationComplete.mutation.gql'
import updateEmailMutation from './gql/updateEmail.mutation.gql'
import userQuery from './gql/user.query.gql'
import { InnerProps } from './types'

const emailAlreadyTaken = graphQLErrors => {
  return graphQLErrors.some(graphQLError =>
    pathOr([], ['data', 'error'], graphQLError).includes('has already been taken'),
  )
}

export const configureApolloModal = (graphQLErrors, closeErrorModal, values) => {
  let errorDetails = {}

  if (emailAlreadyTaken(graphQLErrors)) {
    errorDetails = {
      actions: [
        {
          label: 'Try Again',
          handler: () => {
            closeErrorModal()
          },
          type: ModalActionTypes.primary,
        },
      ],
      message: `It looks like you already have an account registered with ${values.email}`,
      title: 'Already Registered',
    }
  }
  return errorDetails
}

export const enhance = compose<InnerProps, {}>(
  withEnv,
  withFlowRouter,
  withRouter,
  withPartnerProps,
  withOptimizely,
  withTieredRegistration,
  withReferralDisclosureRequired(
    ({ route, referralDisclosureRequiredLoading, referralDisclosureRequired }) => {
      if (!referralDisclosureRequiredLoading && referralDisclosureRequired) {
        route({ from: FlowRoute.Success, action: 'referral-reward' })
      }
    },
  ),
  branch(
    ({ referralDisclosureRequiredLoading, referralDisclosureRequired }) =>
      referralDisclosureRequiredLoading || referralDisclosureRequired,
    renderNothing,
  ),
  graphql(userQuery),
  graphql(updateEmailMutation, { name: 'updateEmail' }),
  graphql(registrationComplete, { name: 'registrationComplete' }),
  withState('submitting', 'setSubmitting', false),
  withState('changeEmail', 'setChangeEmail', false),
  withState('updatedEmail', 'setUpdatedEmail', ''),
  withState('alreadyRegisteredEmail', 'setAlreadyRegisteredEmail', ''),
  withProps(({ data, values, partner, location }) => {
    let props = {
      loading: data.loading,
      hasInvested: pathOr(null, ['state', 'investment', 'amount'], location) !== null,
      investedAmount: pathOr(null, ['state', 'investment', 'amount'], location),
      investedFrequency: pathOr(null, ['state', 'investment', 'frequency'], location),
    }

    if (!data.loading) {
      if (data.user) {
        props = {
          ...props,
          ...{
            email: data.user.email,
            rows: formRows(data),
            values,
          },
        }
      }
      if (partner) {
        props = {
          ...props,
          ...{
            redirectUrl: window.sessionStorage.getItem(REDIRECTION_URL_KEY),
            partnerName: partner.org.name ? partner.org.name : null,
          },
        }
      }
    }

    return props
  }),
  withFormik({
    mapPropsToValues,
    enableReinitialize: true,
    validationSchema,
    handleSubmit,
    validateOnChange: false,
  }),
  lifecycle<any, {}>({
    componentDidMount() {
      document.cookie = `FROM_REGISTRATION=true; domain=${domainMap.get(
        process.env.BUILD_ENV,
      )}; path=/ `
      if (
        window.gtag &&
        this.props.env &&
        this.props.env.get('optimizelyEnvironment') === 'production'
      ) {
        window.gtag('event', 'conversion', { send_to: 'AW-965352741/EXenCOTtn6MBEKW6qMwD' })
      }
      this.props.registrationComplete().then(() => {
        trackEvent('Funnel Complete', {
          eventData: {
            event: 'funnel_complete',
            event_text: 'Funnel Complete',
          },
          flow: this.props.flow,
          partner: this.props.partner,
        })
        setTimeout(() => {
          if (this.props.partner && this.props.partner.org.name === 'Groupon') {
            return this.props.history.push(`${this.props.env.get('webAppUrl')}/redeem`)
          } else {
            return this.props.redirectUrl && (window.location = this.props.redirectUrl)
          }
        }, 2000)
      })
      this.props.trackOptimizely('registration.complete')
    },
  }),
  withHandlers({
    handleChangeEmail: ({ setChangeEmail, changeEmail }) => () => {
      setChangeEmail(!changeEmail)
    },
    handleCancelChangeEmail: ({ setChangeEmail, changeEmail }) => () => {
      setChangeEmail(!changeEmail)
    },
    setEmailFieldValue: ({ setFieldValue }) => (field, value) => {
      setFieldValue(field, value)
    },
    handlePartnerRedirect: ({ redirectUrl }) => () => {
      window.location.replace(redirectUrl)
    },
    handleOnLink: ({ env, flow }) => () => {
      const url =
        flow === 'laterOnboarding' ? env.get('webAppUrl') + '/invest/later' : env.get('webAppUrl')
      window.location.replace(url)
    },
  }),
  withApolloErrorHandler(
    'updateEmail',
    ({ closeErrorModal, values }) => ({ graphQLErrors = [] }) => {
      return configureApolloModal(graphQLErrors, closeErrorModal, values)
    },
  ),
)
